<template>
  <div>
    <h2 class="primary--text mb-3">
      Dashboard
    </h2>

    <v-row>
      <v-col
        cols="12"
        sm="4"
      >
        <v-card outlined>
          <v-card-text>
            <div class="d-flex justify-space-between">
              <div>
                Total Routes

                <v-progress-circular
                  v-if="isLoading"
                  indeterminate
                  color="primary"
                  class="d-block my-1"
                />
                <div
                  v-else
                  class="text-h4 font-weight-bold primary--text d-flex"
                >
                  {{ totalAccepted + totalDelivered }}
                  <span class="caption grey--text ma-2">this week</span>
                </div>
              </div>

              <v-icon
                class="px-4 primary rounded"
                color="white"
              >
                mdi-truck-outline
              </v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        sm="4"
      >
        <v-card outlined>
          <v-card-text>
            <div class="d-flex justify-space-between">
              <div>
                Total Routes Accepted

                <v-progress-circular
                  v-if="isLoading"
                  indeterminate
                  color="pink"
                  class="d-block my-1"
                />
                <div
                  v-else
                  class="text-h4 font-weight-bold pink--text d-flex"
                >
                  {{ totalAccepted }}
                  <span class="caption grey--text ma-2">this week</span>
                </div>
              </div>

              <v-icon
                class="px-4 pink rounded"
                color="white"
              >
                mdi-truck-fast-outline
              </v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        sm="4"
      >
        <v-card outlined>
          <v-card-text>
            <div class="d-flex justify-space-between">
              <div>
                Total Routes Delivered

                <v-progress-circular
                  v-if="isLoading"
                  indeterminate
                  color="green"
                  class="d-block my-1"
                />
                <div
                  v-else
                  class="text-h4 font-weight-bold green--text d-flex"
                >
                  {{ totalDelivered }}
                  <span class="caption grey--text ma-2">this week</span>
                </div>
              </div>

              <v-icon
                class="px-4 green rounded"
                color="white"
              >
                mdi-truck-check-outline
              </v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="pa-0">
            <v-list-item>
              <v-list-item-avatar class="mr-1">
                <v-icon v-text="'mdi-information-outline'" />
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title v-text="'Routes this week'" />
                <v-list-item-subtitle
                  class="caption"
                  v-text="
                    'The chart below displays number of Delivered and Accepted routes this week.'
                  "
                />
              </v-list-item-content>
            </v-list-item>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <div
              v-if="isLoading"
              class="d-flex pa-12"
            >
              <v-progress-circular
                class=" mx-auto"
                :size="70"
                color="primary"
                :width="7"
                indeterminate
              />
            </div>

            <bar-chart
              v-if="!isLoading"
              :height="160"
              :labels="labels"
              :datasets="datasets"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="snackbar.isOpen"
      :color="snackbar.color"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { parseRoute } from '@/utils/documentParser';
import { humanizeDateFromSeconds } from '@/utils/misc';
import BarChart from '@/components/dashboard/BarChart.vue';
import { routesCollection, secureAuthSupplier } from '@/firebase';

export default {
  components: { BarChart },
  data() {
    return {
      routes: null,
      isLoading: false,
      snackbar: {
        text: '',
        isOpen: false,
        color: 'green',
      },
    };
  },
  computed: {
    labels() {
      return Object.keys(this.dates);
    },
    datasets() {
      if (!this.routes) {
        return [];
      }

      const dates = Object.values(this.dates);
      return [
        {
          label: 'Accepted',
          backgroundColor: '#e65587',
          data: dates.map((date) => this.routes[date].Accepted.length),
        },
        {
          label: 'Delivered',
          backgroundColor: '#57ae18',
          data: dates.map((date) => this.routes[date].Completed.length),
        },
      ];
    },

    totalDelivered() {
      if (!this.datasets.length) {
        return 0;
      }

      return this.datasets[1].data.reduce((sum, num) => sum + num);
    },
    totalAccepted() {
      if (!this.datasets.length) {
        return 0;
      }

      return this.datasets[0].data.reduce((sum, num) => sum + num);
    },
    dates() {
      const moment = require('moment');

      const startOfWeek = moment().startOf('week');
      const endOfWeek = moment().endOf('week');

      const days = {};
      let day = startOfWeek;

      while (day <= endOfWeek) {
        days[day.format('dddd')] = day.format('YYYY-MM-DD');
        day = day.clone().add(1, 'd');
      }

      return days;
    },
  },
  created() {
    this.fetchRoutes();
  },
  methods: {
    fetchRoutes() {
      this.isLoading = true;

      const query = this.generateFilters(routesCollection);

      query
        .get()
        .then((querySnapshot) => {
          this.isLoading = false;

          const routes = querySnapshot.docs.map(parseRoute);

          this.routes = this.groupByDateAndStatus(routes);
        })
        .catch(() => {
          this.isLoading = false;

          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
        });
    },
    generateFilters(collection) {
      let query = secureAuthSupplier(collection);

      const start = new Date(this.dates.Sunday);
      const end = new Date(this.dates.Saturday);

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      query = query
        .where('created_at', '>=', start)
        .where('created_at', '<=', end);

      return query;
    },
    groupByDateAndStatus(routes) {
      const dates = Object.values(this.dates);

      const result = dates.reduce((acc, date) => (
        { ...acc, [date]: { Completed: [], Accepted: [] } }
      ), {});

      routes.forEach((route) => {
        const orderDate = humanizeDateFromSeconds(route.createdAt, 'YYYY-MM-DD');
        if (result[orderDate][route.status]) {
          result[orderDate][route.status].push(route);
        }
      });

      return result;
    },
    displaySnackbar({ text, color }) {
      this.snackbar = {
        text,
        color,
        isOpen: true,
      };
    },
  },
};
</script>
